import { FC, useCallback, useEffect, useState } from 'react';
import {
  CashIcon,
  ChevronDownIcon,
  DownloadIcon,
  LinkIcon,
  MailIcon,
  XIcon,
} from '@heroicons/react/outline';

import { useProviderPaymentActivitiesLazyQuery } from '../../../../generated/graphql';
import { getAuditActivityTeamMemberDisplay } from '../../../lib/auditActivityDisplay';
import { getFullDateTimeStamp, getRelativeTimestamp } from '../../../lib/time';
import Avatar from '../../Avatar';
import ModalDialog from '../../ModalDialog';
import Spinner from '../../../svgs/Spinner';

const PAGE_SIZE = 20;

const ACTION_LABEL: Record<string, string> = {
  invoice_created: 'Invoice created',
  invoice_reminder_sent: 'Reminder sent',
  invoice_voided: 'Invoice voided',
  invoice_paid: 'Invoice paid',
  invoice_downloaded: 'Invoice downloaded',
  invoice_list_exported: 'Invoice list exported',
  stripe_connected: 'Stripe connected',
  stripe_disconnected: 'Stripe disconnected',
};

type ExtraMetadata = {
  patient_name?: string;
  stripe_invoice_number?: string;
  amount?: number;
  currency?: string;
  status?: string;
  previous_status?: string;
  source_page?: string;
  note?: string;
  source?: string;
  payment_method?: string;
  invoice_count?: number;
  filtered?: boolean;
};

const getActionIcon = (activityType: string) => {
  const iconClass = 'h-3.5 w-3.5 shrink-0';
  switch (activityType) {
    case 'invoice_created':
    case 'invoice_paid':
      return <CashIcon className={iconClass} />;
    case 'invoice_reminder_sent':
      return <MailIcon className={iconClass} />;
    case 'invoice_voided':
      return <XIcon className={iconClass} />;
    case 'invoice_downloaded':
    case 'invoice_list_exported':
      return <DownloadIcon className={iconClass} />;
    case 'stripe_connected':
    case 'stripe_disconnected':
      return <LinkIcon className={iconClass} />;
    default:
      return <CashIcon className={iconClass} />;
  }
};

const getInvoiceLabel = (
  activityType: string,
  metadata: ExtraMetadata | null,
): string => {
  if (activityType === 'invoice_list_exported') {
    return 'Invoice list';
  }
  if (!metadata) return '—';
  if (metadata.stripe_invoice_number && metadata.patient_name) {
    return `${metadata.stripe_invoice_number} · ${metadata.patient_name}`;
  }
  return metadata.stripe_invoice_number || metadata.patient_name || '—';
};

const getDetailsForActivity = (
  activityType: string,
  metadata: ExtraMetadata | null,
): string => {
  if (!metadata) return '—';

  switch (activityType) {
    case 'invoice_created':
      if (metadata.note) return metadata.note;
      return metadata.source === 'manual' ? 'Manual invoice' : '—';
    case 'invoice_paid': {
      const methodLabels: Record<string, string> = {
        BankTransfer: 'Bank transfer',
        Check: 'Check',
        Cash: 'Cash',
        Other: 'Other',
      };
      const methodLabel = metadata.payment_method
        ? methodLabels[metadata.payment_method] || metadata.payment_method
        : null;
      if (methodLabel && metadata.note) {
        return `${methodLabel} · ${metadata.note}`;
      }
      if (methodLabel) return methodLabel;
      if (metadata.amount != null && metadata.currency) {
        return `${metadata.amount} ${metadata.currency.toUpperCase()}`;
      }
      return '—';
    }
    case 'invoice_downloaded':
      return metadata.source_page === 'patient_profile'
        ? 'Downloaded from client profile'
        : 'Downloaded from Payments';
    case 'invoice_list_exported': {
      const count = metadata.invoice_count;
      const countLabel =
        typeof count === 'number'
          ? `${count} invoice${count === 1 ? '' : 's'}`
          : 'Invoice list';
      return metadata.filtered ? `${countLabel} (filtered)` : countLabel;
    }
    case 'invoice_voided':
      return metadata.previous_status
        ? `Previous status: ${metadata.previous_status}`
        : '—';
    case 'stripe_connected':
      return 'Stripe account connected';
    case 'stripe_disconnected':
      return 'Stripe account disconnected';
    default:
      if (metadata.amount != null && metadata.currency) {
        return `${metadata.amount} ${metadata.currency.toUpperCase()}`;
      }
      return '—';
  }
};

type Activity = {
  id: string;
  activityType: string;
  actorType?: string | null;
  performedByProviderUser?: {
    id: string;
    name?: string | null;
    profileImageMedia?: { url: string } | null;
  } | null;
  performedAt: string;
  extraMetadata?: unknown;
};

type PaymentsActivityModalProps = {
  isOpen: boolean;
  setClosed: () => void;
};

const PaymentsActivityModal: FC<PaymentsActivityModalProps> = ({
  isOpen,
  setClosed,
}) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fetchActivities, { loading }] = useProviderPaymentActivitiesLazyQuery({
    fetchPolicy: 'network-only',
  });

  const loadPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      setLoadError(null);
      const result = await fetchActivities({
        variables: { limit: PAGE_SIZE, offset: pageOffset },
      });
      if (result.error) {
        setLoadError('Unable to load Payments activity. Please try again.');
        setHasMore(false);
        return 0;
      }
      const next = (result.data?.providerPaymentActivities ?? []) as Activity[];
      setAllActivities((prev) => (append ? [...prev, ...next] : next));
      setOffset(pageOffset + next.length);
      setHasMore(next.length >= PAGE_SIZE);
      return next.length;
    },
    [fetchActivities],
  );

  useEffect(() => {
    if (isOpen) {
      setOffset(0);
      setAllActivities([]);
      setHasMore(true);
      setLoadError(null);
      loadPage(0, false);
    }
  }, [isOpen, loadPage]);

  const handleLoadMore = useCallback(() => {
    loadPage(offset, true);
  }, [loadPage, offset]);

  return (
    <ModalDialog
      title="Payments activity"
      isOpen={isOpen}
      setClosed={setClosed}
      fetching={false}
      width="large"
      overflow="auto"
      hideFooter
      bodyClassName="min-h-[200px]"
      className="sm:min-w-[900px]"
    >
      <div className="flex flex-col">
        {loading && allActivities.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            {allActivities.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-neutral-75">
                <table className="w-full min-w-[900px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-neutral-75 bg-neutral-25">
                      <th className="whitespace-nowrap px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Team member
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Action
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Details
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allActivities.map((activity) => {
                      const teamMember =
                        getAuditActivityTeamMemberDisplay(activity);
                      const actionLabel =
                        ACTION_LABEL[activity.activityType] ??
                        activity.activityType;
                      const metadata = (activity.extraMetadata ??
                        null) as ExtraMetadata | null;
                      const invoiceLabel = getInvoiceLabel(
                        activity.activityType,
                        metadata,
                      );
                      const details = getDetailsForActivity(
                        activity.activityType,
                        metadata,
                      );

                      return (
                        <tr
                          key={activity.id}
                          className={
                            teamMember.isSystem
                              ? 'border-b border-neutral-50 bg-secondary-20 last:border-b-0'
                              : 'border-b border-neutral-50 last:border-b-0'
                          }
                        >
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex items-center gap-2">
                              {teamMember.isSystem ? (
                                <div
                                  className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-0.5"
                                  title="Homecoming"
                                >
                                  <img
                                    src={teamMember.imageUrl}
                                    alt="Homecoming"
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                              ) : (
                                <Avatar
                                  size="small"
                                  imageUrl={teamMember.imageUrl}
                                  name={teamMember.name}
                                />
                              )}
                              <div className="min-w-0">
                                <span className="block text-caption text-neutral-150">
                                  {teamMember.name}
                                </span>
                                {teamMember.subtitle && (
                                  <span className="block text-small-caption text-secondary-125">
                                    {teamMember.subtitle}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={
                                teamMember.isSystem
                                  ? 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-25 px-2 py-0.5 text-small-label font-medium text-secondary-150'
                                  : 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-50 px-2 py-0.5 text-small-label font-medium text-neutral-150'
                              }
                            >
                              {getActionIcon(activity.activityType)}
                              {actionLabel}
                            </span>
                          </td>
                          <td className="max-w-[220px] px-4 py-3 text-caption text-neutral-150">
                            {invoiceLabel}
                          </td>
                          <td className="max-w-[280px] px-4 py-3 text-small-caption text-neutral-125">
                            {details}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-small-caption text-neutral-125">
                            <span
                              title={
                                activity.performedAt
                                  ? getFullDateTimeStamp(activity.performedAt)
                                  : ''
                              }
                            >
                              {activity.performedAt
                                ? getRelativeTimestamp(activity.performedAt)
                                : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {allActivities.length > 0 && hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="flex items-center gap-1 text-caption font-medium text-secondary-100 hover:text-secondary-150 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Spinner className="h-4 w-4" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load more
                      <ChevronDownIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
            {!loading && loadError && (
              <div className="py-12 text-center text-caption text-red-100">
                {loadError}
              </div>
            )}
            {!loading && !loadError && allActivities.length === 0 && (
              <div className="py-12 text-center text-caption text-neutral-125">
                No Payments activity yet.
              </div>
            )}
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default PaymentsActivityModal;
